/**
 * CardQualityPage — bird's-eye dashboard above /admin/needs-review.
 *
 * Fetches /api/admin/card-quality once on mount and renders six panels:
 *   1. Overview totals (total / passed / needs_review / pending)
 *   2. Vision-ingest progress + version distribution
 *   3. needs_review by error bucket (vision-auth, anthropic-transient,
 *      sonnet/opus runtime flags, field disagreement, etc.)
 *   4. Residual per-field disagreement count (top 12)
 *   5. Auto-promote daily history (script that flips eval_status when
 *      Sonnet+Opus agree)
 *   6. Modeled Anthropic spend
 *
 * Click "Refresh" to re-fetch. No write actions live here — for triage
 * of an individual card, follow the link to /admin/needs-review.
 */

import { type ReactElement, useCallback, useEffect, useState } from 'react';
import { apiPath } from '../api.js';

type CardQualityPayload = {
  ok: boolean;
  generated_at: number;
  game_id: string;
  overview: {
    total: number;
    passed: number;
    needs_review: number;
    pending: number;
    failed: number;
  };
  vision: {
    sonnet_only: number;
    sonnet_plus_opus: number;
    failed: number;
    not_run: number;
  };
  needs_review_buckets: Record<string, number>;
  residual_fields: ReadonlyArray<{ field: string; count: number }>;
  auto_promote: {
    total_cleared: number;
    by_day: ReadonlyArray<{ day: string; count: number }>;
  };
  cost: {
    sonnet_calls: number;
    opus_calls: number;
    sonnet_usd: number;
    opus_usd: number;
    total_usd: number;
    per_call_usd: { vision_sonnet: number; vision_opus: number };
    note: string;
  };
  error?: string;
};

const BUCKET_LABELS: Record<string, string> = {
  vision_auth_failure: 'Vision auth failure (stale)',
  vision_image_404: 'Image not found in R2',
  anthropic_credit_exhausted: 'Anthropic credit exhausted',
  anthropic_transient: 'Anthropic transient (5xx/429/conn)',
  other_ingest_error: 'Other ingest error',
  sonnet_runtime_flag: 'Sonnet runtime compliance flag',
  opus_runtime_flag: 'Opus runtime compliance flag',
  vision_field_disagreement: 'Vision field disagreement',
  no_detail: 'No error/note (manual flag?)',
};

export function CardQualityPage(): ReactElement {
  const [data, setData] = useState<CardQualityPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(apiPath('/api/admin/card-quality'), {
        credentials: 'include',
      });
      const body = (await res.json()) as CardQualityPayload;
      if (body.ok) setData(body);
      else setError(body.error ?? 'unknown error');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <div className="mx-auto max-w-6xl p-6 text-cosmos-100">
      <header className="mb-6 flex items-baseline justify-between">
        <h1 className="font-bold text-2xl text-amber-200">Card quality dashboard</h1>
        <div className="flex items-baseline gap-3">
          {data && (
            <span className="text-cosmos-400 text-xs">
              generated {new Date(data.generated_at).toLocaleString()}
            </span>
          )}
          <button
            type="button"
            onClick={() => void reload()}
            disabled={loading}
            className="rounded bg-cosmos-800 px-3 py-1 text-sm hover:bg-cosmos-700 disabled:opacity-50"
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </header>

      {error && (
        <div className="mb-4 rounded border border-rose-700 bg-rose-950/40 px-3 py-2 text-rose-200 text-sm">
          {error}
        </div>
      )}

      {data && (
        <>
          <Overview overview={data.overview} />
          <Vision vision={data.vision} totalCards={data.overview.total} />
          <NeedsReviewBuckets buckets={data.needs_review_buckets} />
          <ResidualFields fields={data.residual_fields} />
          <AutoPromoteHistory history={data.auto_promote} />
          <CostPanel cost={data.cost} />
        </>
      )}
    </div>
  );
}

function Overview({ overview }: { overview: CardQualityPayload['overview'] }): ReactElement {
  const passedPct = overview.total > 0 ? (100 * overview.passed) / overview.total : 0;
  const reviewPct = overview.total > 0 ? (100 * overview.needs_review) / overview.total : 0;
  return (
    <Section title="Overview">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Total cards" value={overview.total} />
        <Stat
          label="Passed"
          value={overview.passed}
          suffix={`${passedPct.toFixed(0)}%`}
          accent="emerald"
        />
        <Stat
          label="Needs review"
          value={overview.needs_review}
          suffix={`${reviewPct.toFixed(0)}%`}
          accent="amber"
        />
        <Stat label="Pending" value={overview.pending} accent="cosmos" />
      </div>
    </Section>
  );
}

function Vision({
  vision,
  totalCards,
}: {
  vision: CardQualityPayload['vision'];
  totalCards: number;
}): ReactElement {
  const visionTotal = vision.sonnet_only + vision.sonnet_plus_opus + vision.failed;
  const successPct =
    visionTotal > 0 ? (100 * (vision.sonnet_only + vision.sonnet_plus_opus)) / visionTotal : 0;
  return (
    <Section title="Vision ingest">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Sonnet only" value={vision.sonnet_only} />
        <Stat label="Sonnet + Opus" value={vision.sonnet_plus_opus} />
        <Stat label="Failed" value={vision.failed} accent={vision.failed > 0 ? 'rose' : 'cosmos'} />
        <Stat label="Not run" value={vision.not_run} accent="cosmos" />
      </div>
      <p className="mt-3 text-cosmos-400 text-sm">
        Of {visionTotal.toLocaleString()} vision-attempted cards,{' '}
        <span className="text-emerald-300">{successPct.toFixed(1)}%</span> completed cleanly.
        {vision.not_run > 0 && (
          <> The {vision.not_run.toLocaleString()} not-run rows came from the text-only pipeline.</>
        )}
        {totalCards > 0 && <> Catalog size: {totalCards.toLocaleString()} cards.</>}
      </p>
    </Section>
  );
}

function NeedsReviewBuckets({ buckets }: { buckets: Record<string, number> }): ReactElement {
  const entries = Object.entries(buckets).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((acc, [, n]) => acc + n, 0);
  return (
    <Section title="needs_review by bucket">
      {total === 0 ? (
        <p className="text-cosmos-400 text-sm">Queue empty.</p>
      ) : (
        <table className="w-full font-mono text-sm">
          <thead className="text-cosmos-400 text-xs uppercase">
            <tr>
              <th className="pb-1 text-left">Bucket</th>
              <th className="pb-1 text-right">Count</th>
              <th className="pb-1 text-right">Share</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([bucket, n]) => (
              <tr key={bucket} className="border-cosmos-800 border-t">
                <td className="py-1">{BUCKET_LABELS[bucket] ?? bucket}</td>
                <td className="py-1 text-right">{n.toLocaleString()}</td>
                <td className="py-1 text-right text-cosmos-400">
                  {((100 * n) / total).toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

function ResidualFields({
  fields,
}: {
  fields: CardQualityPayload['residual_fields'];
}): ReactElement {
  if (fields.length === 0) {
    return (
      <Section title="Residual field disagreements">
        <p className="text-cosmos-400 text-sm">None.</p>
      </Section>
    );
  }
  const top = fields.slice(0, 12);
  const max = top[0]?.count ?? 1;
  return (
    <Section
      title="Residual field disagreements"
      hint="Per-field count of needs_review verdicts that auto-promote couldn't clear (sonnet/opus disagree, or Opus skipped on a vision-only field)."
    >
      <div className="space-y-1">
        {top.map(({ field, count }) => (
          <div key={field} className="flex items-center gap-3 font-mono text-sm">
            <span className="w-32 text-cosmos-200">{field}</span>
            <div className="h-3 flex-1 overflow-hidden rounded bg-cosmos-900">
              <div
                className="h-full bg-amber-700"
                style={{ width: `${Math.max(2, (100 * count) / max)}%` }}
              />
            </div>
            <span className="w-12 text-right text-cosmos-400">{count}</span>
          </div>
        ))}
      </div>
    </Section>
  );
}

function AutoPromoteHistory({
  history,
}: {
  history: CardQualityPayload['auto_promote'];
}): ReactElement {
  return (
    <Section
      title="Auto-promote history"
      hint="Rows cleared by scripts/auto-promote-vision-verdicts.mjs (Sonnet+Opus agreement over text)."
    >
      <p className="mb-2 text-sm">
        <span className="text-cosmos-400">Total cleared:</span>{' '}
        <span className="font-mono text-emerald-300">{history.total_cleared.toLocaleString()}</span>
      </p>
      {history.by_day.length === 0 ? (
        <p className="text-cosmos-400 text-sm">Never run.</p>
      ) : (
        <div className="space-y-1 font-mono text-sm">
          {history.by_day.map(({ day, count }) => (
            <div key={day} className="flex items-baseline gap-3">
              <span className="w-28 text-cosmos-300">{day}</span>
              <span className="text-cosmos-100">{count.toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function CostPanel({ cost }: { cost: CardQualityPayload['cost'] }): ReactElement {
  return (
    <Section title="Modeled Anthropic spend" hint={cost.note}>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Sonnet calls" value={cost.sonnet_calls} />
        <Stat label="Opus calls" value={cost.opus_calls} />
        <Stat label="Sonnet ($)" value={cost.sonnet_usd} formatter={fmtUsd} />
        <Stat label="Opus ($)" value={cost.opus_usd} formatter={fmtUsd} />
      </div>
      <div className="mt-3 flex items-baseline justify-between border-cosmos-800 border-t pt-3">
        <span className="text-cosmos-400 text-sm">Total estimate</span>
        <span className="font-mono text-amber-200 text-lg">{fmtUsd(cost.total_usd)}</span>
      </div>
      <p className="mt-2 text-cosmos-500 text-xs">
        Per-call assumptions: Sonnet {fmtUsd(cost.per_call_usd.vision_sonnet)} · Opus{' '}
        {fmtUsd(cost.per_call_usd.vision_opus)}
      </p>
    </Section>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}): ReactElement {
  return (
    <section className="mb-6 rounded border border-cosmos-800 bg-cosmos-950/40 p-4">
      <h2 className="mb-3 font-semibold text-amber-200 text-lg">{title}</h2>
      {hint && <p className="mb-3 text-cosmos-400 text-xs">{hint}</p>}
      {children}
    </section>
  );
}

function Stat({
  label,
  value,
  suffix,
  accent,
  formatter,
}: {
  label: string;
  value: number;
  suffix?: string;
  accent?: 'amber' | 'emerald' | 'rose' | 'cosmos';
  formatter?: (n: number) => string;
}): ReactElement {
  const accentClass =
    accent === 'amber'
      ? 'text-amber-300'
      : accent === 'emerald'
        ? 'text-emerald-300'
        : accent === 'rose'
          ? 'text-rose-300'
          : 'text-cosmos-100';
  return (
    <div className="rounded bg-cosmos-900/60 p-3">
      <div className="text-cosmos-400 text-xs uppercase tracking-wide">{label}</div>
      <div className={`mt-1 font-mono text-xl ${accentClass}`}>
        {formatter ? formatter(value) : value.toLocaleString()}
        {suffix && <span className="ml-2 text-cosmos-400 text-sm">{suffix}</span>}
      </div>
    </div>
  );
}

function fmtUsd(n: number): string {
  return `$${n.toFixed(2)}`;
}
